import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  makeStyles
} from '@material-ui/core';
import { Settings, Close, CloudUpload, GetApp } from '@material-ui/icons';
import { useTranslation } from '../hooks/useTranslation';
import { useAuth } from '../context/ApiKeyContext';
import CustomizationSliders from './CustomizationSliders';
import '../pages/Contestacao.css'; // Importar estilos dos sliders
import {
  CONTESTATION_SERVICE,
  readServiceCustomization,
  checkServiceCustomizationExists,
  writeServiceCustomization,
  fileToBase64,
  downloadBase64File
} from '../utils/serviceCustomizationApi';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid var(--ikaro-gray-medium)',
    padding: theme.spacing(2, 3),
  },
  titleIcon: {
    marginRight: theme.spacing(1),
    color: 'var(--ikaro-orange-primary)',
  },
  titleText: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  titleCloseButton: {
    minWidth: 'auto',
    padding: theme.spacing(1),
    color: 'var(--ikaro-gray-dark)',
  },
  dialogContent: {
    padding: 0,
    minWidth: '800px',
    height: '70vh',
    display: 'flex',
  },
  sidebar: {
    width: '200px',
    backgroundColor: 'var(--ikaro-gray-light)',
    borderRight: '1px solid var(--ikaro-gray-medium)',
    display: 'flex',
    flexDirection: 'column',
  },
  sidebarItem: {
    padding: theme.spacing(2),
    cursor: 'pointer',
    borderBottom: '1px solid var(--ikaro-gray-medium)',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: 'var(--ikaro-orange-light)',
    },
    '&.active': {
      backgroundColor: 'var(--ikaro-orange-primary)',
      color: 'white',
      fontWeight: 'bold',
    },
  },
  contentArea: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: 'auto',
  },
  subtitle: {
    color: 'var(--ikaro-gray-dark)',
    marginBottom: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    color: 'var(--ikaro-orange-primary)',
    display: 'flex',
    alignItems: 'center',
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
  },
  textField: {
    marginBottom: theme.spacing(2),
    '& .MuiInputBase-root': {
      backgroundColor: 'var(--ikaro-text-white)',
    },
  },
  textArea: {
    marginBottom: theme.spacing(2),
    '& .MuiInputBase-root': {
      backgroundColor: 'var(--ikaro-text-white)',
    },
    '& .MuiInputBase-input': {
      minHeight: '80px',
    },
  },
  fileUploadSection: {
    border: '2px dashed var(--ikaro-gray-medium)',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
    textAlign: 'center',
    backgroundColor: 'var(--ikaro-gray-light)',
    marginBottom: theme.spacing(2),
    transition: 'all 0.2s',
    '&:hover': {
      borderColor: 'var(--ikaro-orange-primary)',
      backgroundColor: 'var(--ikaro-orange-light)',
    },
    '&.dragover': {
      borderColor: 'var(--ikaro-orange-primary)',
      backgroundColor: 'var(--ikaro-orange-light)',
    },
  },
  fileInput: {
    display: 'none',
  },
  fileButton: {
    backgroundColor: 'var(--ikaro-orange-primary)',
    color: 'white',
    marginBottom: theme.spacing(1),
    '&:hover': {
      backgroundColor: 'var(--ikaro-orange-hover)',
    },
  },
  fileInfo: {
    fontSize: '14px',
    color: 'var(--ikaro-gray-dark)',
  },
  currentFile: {
    backgroundColor: 'var(--ikaro-text-white)',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(0.5),
    border: '1px solid var(--ikaro-gray-medium)',
    marginBottom: theme.spacing(2),
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: '1px solid var(--ikaro-gray-medium)',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  actionGroup: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  closeButton: {
    color: 'var(--ikaro-gray-dark)',
    borderColor: 'var(--ikaro-gray-medium)',
  },
  saveButton: {
    backgroundColor: 'var(--ikaro-orange-primary)',
    color: 'white',
    '&:hover': {
      backgroundColor: 'var(--ikaro-orange-hover)',
    },
  },
  templateButton: {
    backgroundColor: 'var(--ikaro-purple-accent)',
    color: 'white',
    '&:hover': {
      backgroundColor: 'var(--ikaro-purple-hover)',
    },
  },
  sectionSaveButton: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.5, 3),
  },
}));

const CellParamsModal = ({ 
  open, 
  onClose, 
  cellId, 
  showSaveButtons = true, // Controla se mostra os botões de salvar
  onSave = null, // Callback para quando salvar (modo externo)
  customizationParams: externalCustomizationParams = null, // Parâmetros externos
  observations: externalObservations = null, // Observações externas
  onParamsChange = null, // Callback para mudanças nos parâmetros
  onObservationsChange = null // Callback para mudanças nas observações
}) => {
  const classes = useStyles();
  const { t } = useTranslation('jobs');
  const { token, user } = useAuth();
  
  // Estados para os parâmetros de personalização (usa externos se fornecidos)
  const [customizationParams, setCustomizationParams] = useState(
    externalCustomizationParams || {
      creativity: 2,
      tone: 2,
      scope_topics: 2,
      complexity: 2,
      foundation_depth: 2,
    }
  );

  // Estados para as observações (usa externos se fornecidos)
  const [observations, setObservations] = useState(
    externalObservations || {
      client: '',
      internal: '',
    }
  );

  // Estados para o template
  const [templateFile, setTemplateFile] = useState(null);
  const [hasTemplate, setHasTemplate] = useState(false);
  const [templateUploading, setTemplateUploading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Estado para controlar a aba selecionada
  const [selectedTab, setSelectedTab] = useState('parameters'); // 'parameters', 'customRules' ou 'template'

  // Função para carregar dados da célula
  const loadCellData = useCallback(async () => {
    if (!cellId || !token) return;
    
    setLoading(true);
    try {
      // Carregar parâmetros de personalização
      const profileParams = await readServiceCustomization(
        token, 
        cellId, 
        CONTESTATION_SERVICE.ATTRIBUTES.PROFILE_PARAMS
      );
      
      // Sempre resetar para valores padrão e depois aplicar os valores da API (mesmo que vazios/nulos)
      const defaultParams = {
        creativity: 2,
        tone: 2,
        scope_topics: 2,
        complexity: 2,
        foundation_depth: 2,
      };
      
      if (profileParams !== null && profileParams !== undefined) {
        // Se a API retornou dados, usar esses dados (mesmo que alguns campos sejam vazios)
        setCustomizationParams({ ...defaultParams, ...profileParams });
      } else {
        // Se a API não retornou dados, usar valores padrão
        setCustomizationParams(defaultParams);
      }

      // Carregar regras do cliente
      const clientRules = await readServiceCustomization(
        token, 
        cellId, 
        CONTESTATION_SERVICE.ATTRIBUTES.RULES
      );
      
      // Carregar regras internas
      const internalRules = await readServiceCustomization(
        token, 
        cellId, 
        CONTESTATION_SERVICE.ATTRIBUTES.RULES_INTERNAL
      );
      
      // Sempre resetar as observações com os valores da API (mesmo que vazios/nulos)
      setObservations({
        client: clientRules || '',
        internal: internalRules || '',
      });

      // Verificar se existe template
      const templateExists = await checkServiceCustomizationExists(
        token, 
        cellId, 
        CONTESTATION_SERVICE.ATTRIBUTES.DOCX_TEMPLATE_FILE
      );
      
      setHasTemplate(templateExists);

    } catch (error) {
      console.error('Erro ao carregar dados da célula:', error);
    } finally {
      setLoading(false);
    }
  }, [cellId, token]);

  // Reset inicial e carregar dados da célula
  useEffect(() => {
    if (open) {
      // Se não está usando valores externos, resetar para padrões antes de carregar
      if (!externalCustomizationParams) {
        setCustomizationParams({
          creativity: 2,
          tone: 2,
          scope_topics: 2,
          complexity: 2,
          foundation_depth: 2,
        });
      }
      
      if (!externalObservations) {
        setObservations({
          client: '',
          internal: '',
        });
      }
      
      // Carregar dados da API apenas se tiver cellId e não estiver usando valores externos
      if (cellId && !externalCustomizationParams && !externalObservations) {
        loadCellData();
      }
    }
  }, [open, cellId, externalCustomizationParams, externalObservations, loadCellData]);

  // Sincronizar com valores externos quando mudarem
  useEffect(() => {
    if (externalCustomizationParams) {
      setCustomizationParams(externalCustomizationParams);
    }
  }, [externalCustomizationParams]);

  useEffect(() => {
    if (externalObservations) {
      setObservations(externalObservations);
    }
  }, [externalObservations]);

  const handleSaveParams = async () => {
    setSaving(true);
    try {
      const success = await writeServiceCustomization(
        token,
        cellId,
        CONTESTATION_SERVICE.ATTRIBUTES.PROFILE_PARAMS,
        customizationParams
      );

      if (success) {
        console.log('Parâmetros salvos com sucesso');
      } else {
        console.error('Erro ao salvar parâmetros');
      }
    } catch (error) {
      console.error('Erro ao salvar parâmetros:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCustomRules = async () => {
    setSaving(true);
    try {
      // Salvar regras do cliente
      const clientSuccess = await writeServiceCustomization(
        token,
        cellId,
        CONTESTATION_SERVICE.ATTRIBUTES.RULES,
        observations.client
      );

      // Salvar regras internas (apenas se o usuário tiver permissão)
      let internalSuccess = true;
      if (hasInternalPermission()) {
        internalSuccess = await writeServiceCustomization(
          token,
          cellId,
          CONTESTATION_SERVICE.ATTRIBUTES.RULES_INTERNAL,
          observations.internal
        );
      }

      if (clientSuccess && internalSuccess) {
        console.log('Regras customizadas salvas com sucesso');
      } else {
        console.error('Erro ao salvar regras customizadas');
      }
    } catch (error) {
      console.error('Erro ao salvar regras customizadas:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateFile) return;

    setTemplateUploading(true);
    try {
      // Converter arquivo para base64
      const base64 = await fileToBase64(templateFile);
      
      // Salvar o arquivo como base64
      const success = await writeServiceCustomization(
        token,
        cellId,
        CONTESTATION_SERVICE.ATTRIBUTES.DOCX_TEMPLATE_FILE,
        base64
      );

      if (success) {
        setHasTemplate(true);
        setTemplateFile(null);
        console.log('Template salvo com sucesso');
      } else {
        console.error('Erro ao salvar template');
      }
    } catch (error) {
      console.error('Erro ao salvar template:', error);
    } finally {
      setTemplateUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const templateBase64 = await readServiceCustomization(
        token,
        cellId,
        CONTESTATION_SERVICE.ATTRIBUTES.DOCX_TEMPLATE_FILE
      );

      if (templateBase64) {
        downloadBase64File(templateBase64, 'template.docx');
      } else {
        console.error('Template não encontrado');
      }
    } catch (error) {
      console.error('Erro ao baixar template:', error);
    }
  };


  const handleObservationChange = (field, value) => {
    const newObservations = {
      ...observations,
      [field]: value,
    };
    setObservations(newObservations);
    
    // Chamar callback externo se fornecido
    if (onObservationsChange) {
      onObservationsChange(newObservations);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      setTemplateFile(file);
    } else {
      alert('Por favor, selecione apenas arquivos DOCX');
    }
  };

  // Verificar se o usuário tem permissão para ver observações internas
  const hasInternalPermission = () => {
    return user && user.permissions && user.permissions.includes('CONTEST_PARAMS_SUPERUSER');
  };



  // Renderizar conteúdo baseado na aba selecionada
  const renderContent = () => {
    if (selectedTab === 'parameters') {
      return (
        <Box>
          <Typography variant="h6" className={classes.sectionTitle}>
            ⚙️ {t('contestacao.cellParams.sections.customization')}
          </Typography>
          
          <CustomizationSliders
            initialValues={customizationParams}
            onChange={(newParams) => {
              setCustomizationParams(newParams);
              // Chamar callback externo se fornecido
              if (onParamsChange) {
                onParamsChange(newParams);
              }
            }}
          />

          {/* Botão de salvar apenas parâmetros (apenas se showSaveButtons for true) */}
          {showSaveButtons && (
            <Button
              onClick={handleSaveParams}
              variant="contained"
              className={`${classes.saveButton} ${classes.sectionSaveButton}`}
              disabled={saving || loading}
              fullWidth
            >
              {saving ? 'Salvando...' : t('contestacao.cellParams.sections.customization')}
            </Button>
          )}
        </Box>
      );
    }

    if (selectedTab === 'customRules') {
      return (
        <Box>
          <Typography variant="h6" className={classes.sectionTitle}>
            📋 {t('contestacao.cellParams.sections.customRules')}
          </Typography>
          
          {/* Regras Customizadas do Cliente */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label={t('contestacao.cellParams.customRules.client')}
            placeholder={t('contestacao.cellParams.customRules.clientPlaceholder')}
            value={observations.client}
            onChange={(e) => handleObservationChange('client', e.target.value)}
            className={classes.textArea}
            variant="outlined"
          />

          {/* Regras Internas (apenas para CONTEST_PARAMS_SUPERUSER) */}
          {hasInternalPermission() && (
            <TextField
              fullWidth
              multiline
              rows={4}
              label={t('contestacao.cellParams.customRules.internal')}
              placeholder={t('contestacao.cellParams.customRules.internalPlaceholder')}
              value={observations.internal}
              onChange={(e) => handleObservationChange('internal', e.target.value)}
              className={classes.textArea}
              variant="outlined"
            />
          )}

          {/* Botão de salvar regras customizadas (apenas se showSaveButtons for true) */}
          {showSaveButtons && (
            <Button
              onClick={handleSaveCustomRules}
              variant="contained"
              className={`${classes.saveButton} ${classes.sectionSaveButton}`}
              disabled={saving || loading}
              fullWidth
            >
              {saving ? 'Salvando...' : t('contestacao.cellParams.customRules.saveRules')}
            </Button>
          )}
        </Box>
      );
    }

    if (selectedTab === 'template') {
      return (
        <Box>
          <Typography variant="h6" className={classes.sectionTitle}>
            📄 {t('contestacao.cellParams.sections.template')}
          </Typography>
          
          <Typography variant="body2" style={{ marginBottom: '16px', color: 'var(--ikaro-gray-dark)' }}>
            {t('contestacao.cellParams.template.description')}
          </Typography>

          {/* Template atual */}
          {hasTemplate && (
            <Box className={classes.currentFile}>
              <Typography variant="body2">
                <strong>Template atual:</strong> template.docx
              </Typography>
              <Button
                onClick={handleDownloadTemplate}
                variant="outlined"
                size="small"
                startIcon={<GetApp />}
                style={{ marginTop: '8px' }}
              >
                Baixar Template Atual
              </Button>
            </Box>
          )}

          {/* Upload de novo template */}
          <Box className={classes.fileUploadSection}>
            <input
              accept=".docx"
              className={classes.fileInput}
              id="template-file-input"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="template-file-input">
              <Button
                variant="contained"
                component="span"
                className={classes.fileButton}
                startIcon={<CloudUpload />}
              >
                {t('contestacao.cellParams.template.selectFile')}
              </Button>
            </label>
            
            {templateFile ? (
              <Typography variant="body2" className={classes.fileInfo}>
                Arquivo selecionado: {templateFile.name}
              </Typography>
            ) : (
              <Typography variant="body2" className={classes.fileInfo}>
                {t('contestacao.cellParams.template.noFile')}
              </Typography>
            )}
          </Box>

          {/* Botão de salvar template (apenas se showSaveButtons for true) */}
          {showSaveButtons && (
            <Button
              onClick={handleSaveTemplate}
              variant="contained"
              className={`${classes.templateButton} ${classes.sectionSaveButton}`}
              disabled={templateUploading || !templateFile}
              startIcon={<CloudUpload />}
              fullWidth
            >
              {templateUploading ? t('contestacao.cellParams.template.uploading') : t('contestacao.cellParams.template.saveTemplate')}
            </Button>
          )}
        </Box>
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Box className={classes.titleText}>
          <Settings className={classes.titleIcon} />
          <Typography variant="h6">
            {t('contestacao.cellParams.title')}
          </Typography>
        </Box>
        <Button
          className={classes.titleCloseButton}
          onClick={onClose}
          size="small"
        >
          <Close />
        </Button>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* Menu lateral */}
        <Box className={classes.sidebar}>
          <Box
            className={`${classes.sidebarItem} ${selectedTab === 'parameters' ? 'active' : ''}`}
            onClick={() => setSelectedTab('parameters')}
          >
            <Typography variant="body1">
              {t('contestacao.cellParams.menu.parameters')}
            </Typography>
          </Box>
          <Box
            className={`${classes.sidebarItem} ${selectedTab === 'customRules' ? 'active' : ''}`}
            onClick={() => setSelectedTab('customRules')}
          >
            <Typography variant="body1">
              {t('contestacao.cellParams.menu.customRules')}
            </Typography>
          </Box>
          <Box
            className={`${classes.sidebarItem} ${selectedTab === 'template' ? 'active' : ''}`}
            onClick={() => setSelectedTab('template')}
          >
            <Typography variant="body1">
              {t('contestacao.cellParams.menu.template')}
            </Typography>
          </Box>
        </Box>

        {/* Área de conteúdo */}
        <Box className={classes.contentArea}>
          {renderContent()}
        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        {/* Apenas botão fechar */}
        <Button
          onClick={onClose}
          variant="outlined"
          className={classes.closeButton}
          disabled={saving || templateUploading}
        >
          {t('contestacao.cellParams.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CellParamsModal;

/*
EXEMPLO DE USO EXTERNO (sem botões de salvar):

const [customizationParams, setCustomizationParams] = useState({
  creativity: 2,
  tone: 2,
  scope_topics: 2,
  complexity: 2,
  foundation_depth: 2,
});

const [observations, setObservations] = useState({
  client: '',
  internal: '',
});

<CellParamsModal
  open={modalOpen}
  onClose={() => setModalOpen(false)}
  cellId={null} // Não precisa de cellId para modo externo
  showSaveButtons={false} // Oculta botões de salvar
  customizationParams={customizationParams}
  observations={observations}
  onParamsChange={setCustomizationParams}
  onObservationsChange={setObservations}
/>

// Os valores ficam sincronizados nos estados externos
// e podem ser usados na chamada da API
*/
